import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

// Use relative URL for API calls - this ensures the request goes to the same domain
// the page is hosted on (works for both preview and production)
const API_URL = process.env.REACT_APP_BACKEND_URL || ''

export default function JoinInvite() {
  const { token } = useParams()
  const [invite, setInvite] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  
  // App store URLs
  const IOS_APP_URL = 'https://apps.apple.com/us/app/squadline/id6756984822'
  const ANDROID_APP_URL = 'https://play.google.com/store/apps/details?id=com.squadline.app'
  
  // Deep link URL scheme
  const APP_DEEP_LINK = `squadline://join/${token}`
  
  useEffect(() => {
    // Fetch invite details
    const fetchInvite = async () => {
      try {
        const response = await fetch(`${API_URL}/api/integrations/invites/${token}`)
        const data = await response.json()
        
        if (data.valid) {
          setInvite(data)
        } else {
          setError(data.error || 'This invite is no longer valid')
        }
      } catch (err) {
        console.error('Error fetching invite:', err)
        setError('Unable to load invite details')
      } finally {
        setLoading(false)
      }
    }
    
    fetchInvite()
  }, [token])
  
  // Detect platform
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
  const isAndroid = /Android/.test(navigator.userAgent)
  const isMobile = isIOS || isAndroid
  
  const handleOpenInApp = () => {
    // Try to open app via deep link - no automatic fallback to store
    window.location.href = APP_DEEP_LINK
  }
  
  const handleDownloadApp = () => {
    // Explicitly go to app store
    if (isIOS) {
      window.location.href = IOS_APP_URL
    } else if (isAndroid) {
      window.location.href = ANDROID_APP_URL
    }
  }
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-200"></div>
      </div>
    )
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-gray-50 rounded-2xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Invite Not Valid</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <p className="text-sm text-gray-600">
            Please contact your team administrator for a new invite link.
          </p>
        </div>
      </div>
    )
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gray-50 border-b border-gray-200 px-4 py-6">
        <div className="max-w-md mx-auto text-center">
          <h1 className="text-2xl font-bold text-white">Squadline</h1>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-md mx-auto px-4 py-8">
        <div className="bg-gray-50 rounded-2xl shadow-lg overflow-hidden">
          {/* Team Info */}
          <div className="bg-gray-50 text-white p-6 text-center">
            <div className="w-16 h-16 bg-gray-50/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-white mb-1">You're Invited!</h2>
            <p className="text-white/80">Join {invite?.team_name || 'the team'} on Squadline</p>
          </div>
          
          {/* Invite Details */}
          <div className="p-6">
            <div className="mb-6">
              <div className="flex items-center justify-between py-3 border-b border-gray-200">
                <span className="text-gray-600">Team</span>
                <span className="font-medium text-white">{invite?.team_name}</span>
              </div>
              {invite?.team_sport && (
                <div className="flex items-center justify-between py-3 border-b border-gray-200">
                  <span className="text-gray-600">Sport</span>
                  <span className="font-medium text-white">{invite?.team_sport}</span>
                </div>
              )}
              <div className="flex items-center justify-between py-3 border-b border-gray-200">
                <span className="text-gray-600">Your Role</span>
                <span className="font-medium text-white capitalize">{invite?.role}</span>
              </div>
            </div>
            
            {/* Open in App Button - for users with app installed */}
            {isMobile && (
              <button
                onClick={handleOpenInApp}
                className="w-full bg-gray-50 text-white py-4 px-6 rounded-xl font-semibold text-lg hover:bg-gray-50 transition-colors mb-4"
              >
                Open in Squadline App
              </button>
            )}
            
            {/* App Store Links */}
            <p className="text-sm text-gray-600 text-center mb-3">
              {isMobile ? "Don't have the app yet?" : "Download the Squadline app:"}
            </p>
            <div className="flex gap-3 justify-center items-center">
              <a
                href={IOS_APP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="h-10"
              >
                <img
                  src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83"
                  alt="Download on the App Store"
                  className="h-10 w-auto"
                />
              </a>
              <a
                href={ANDROID_APP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="h-10"
              >
                <img
                  src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                  alt="Get it on Google Play"
                  className="h-10 w-auto"
                />
              </a>
            </div>
          </div>
          
          {/* Instructions */}
          <div className="bg-gray-50 p-6 border-t border-gray-200">
            <h3 className="font-semibold text-white mb-3">How to Join:</h3>
            <ol className="space-y-2 text-sm text-gray-600">
              <li className="flex gap-2">
                <span className="flex-shrink-0 w-5 h-5 bg-gray-50 text-white rounded-full flex items-center justify-center text-xs">1</span>
                <span>Download the Squadline app</span>
              </li>
              <li className="flex gap-2">
                <span className="flex-shrink-0 w-5 h-5 bg-gray-50 text-white rounded-full flex items-center justify-center text-xs">2</span>
                <span>Create an account or sign in</span>
              </li>
              <li className="flex gap-2">
                <span className="flex-shrink-0 w-5 h-5 bg-gray-50 text-white rounded-full flex items-center justify-center text-xs">3</span>
                <span>Your team will be automatically added!</span>
              </li>
            </ol>
          </div>
        </div>
        
        {/* Footer */}
        <p className="text-center text-sm text-gray-600 mt-6">
          This invite expires on {invite?.expires_at ? new Date(invite.expires_at).toLocaleDateString() : 'N/A'}
        </p>
      </main>
    </div>
  )
}
